import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import Badge from './ui/Badge';
import Button from './ui/Button';
import Card from './ui/Card';
import Select from './ui/Select';
import { useToast } from './ui/Toast';

/**
 * Observabilidade do MCP de UM usuário, DENTRO da página do usuário
 * (`/users/:id`). Antes era um modal aberto por um botão "MCP" na lista de
 * usuários — remanejado 26/08 a pedido do dono: o conteúdo é o mesmo, só saiu do
 * modal e virou uma seção da página. Conexões (cada login é um grant), histórico
 * com filtro por tool/desfecho, resumo de uso e o REVOGAR forçado (o backend
 * recusa o token na chamada seguinte — o portão checa o grant a cada request).
 */
const MCP_TOOLS = ['whoami', 'list_endpoints', 'call_endpoint', 'provider_get', 'navios'];
const OUTCOME_BADGE = { ok: 'success', denied: 'warning', error: 'danger' };

const FormatDateTime = (value) => (value ? new Date(value).toLocaleString('pt-BR') : '—');

const UserMcpPanel = ({ user, canRevoke = false }) => {
    const toast = useToast();
    const [grants, setGrants] = useState([]);
    const [requests, setRequests] = useState([]);
    const [summary, setSummary] = useState(null);
    const [toolFilter, setToolFilter] = useState('');
    const [outcomeFilter, setOutcomeFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [revoking, setRevoking] = useState(null);

    const load = useCallback(async () => {
        if (!user?.id) {
            return;
        }
        setLoading(true);
        try {
            const [grantPage, requestPage, usage] = await Promise.all([
                api.getMcpGrants({ user_id: user.id }),
                api.getMcpRequests({
                    user_id: user.id,
                    tool: toolFilter || undefined,
                    outcome: outcomeFilter || undefined,
                    limit: 100,
                }),
                api.getMcpUsageSummary({ user_id: user.id }),
            ]);
            setGrants(grantPage?.items || []);
            setRequests(requestPage?.items || []);
            setSummary(usage || null);
        } catch (error) {
            toast.error(error?.message || 'Erro ao carregar o uso do MCP');
        } finally {
            setLoading(false);
        }
    }, [user?.id, toolFilter, outcomeFilter, toast]);

    useEffect(() => {
        load();
    }, [load]);

    const handleRevoke = async (grant) => {
        setRevoking(grant.id);
        try {
            await api.revokeMcpGrant(grant.id);
            toast.success('Conexão revogada — o token morre na próxima chamada');
            await load();
        } catch (error) {
            toast.error(error?.message || 'Erro ao revogar');
        } finally {
            setRevoking(null);
        }
    };

    return (
        <Card title="MCP — conexões e uso">
            <div className="space-y-5">
                {/* Resumo — os números que dizem se a ferramenta está sendo usada */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="rounded-lg border border-mono-200 p-3 dark:border-mono-800">
                        <p className="text-xs uppercase tracking-wide text-mono-500">Chamadas</p>
                        <p className="text-2xl font-bold text-black dark:text-white">{summary?.total ?? '—'}</p>
                    </div>
                    <div className="rounded-lg border border-mono-200 p-3 dark:border-mono-800">
                        <p className="text-xs uppercase tracking-wide text-mono-500">Recusadas</p>
                        <p className="text-2xl font-bold text-black dark:text-white">
                            {summary?.by_outcome?.denied ?? '—'}
                        </p>
                    </div>
                    <div className="rounded-lg border border-mono-200 p-3 dark:border-mono-800">
                        <p className="text-xs uppercase tracking-wide text-mono-500">Erros</p>
                        <p className="text-2xl font-bold text-black dark:text-white">
                            {summary?.by_outcome?.error ?? '—'}
                        </p>
                    </div>
                    <div className="rounded-lg border border-mono-200 p-3 dark:border-mono-800">
                        <p className="text-xs uppercase tracking-wide text-mono-500">Duração média</p>
                        <p className="text-2xl font-bold text-black dark:text-white">
                            {summary ? `${summary.avg_duration_ms} ms` : '—'}
                        </p>
                    </div>
                </div>

                {summary && Object.keys(summary.by_tool || {}).length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(summary.by_tool).map(([tool, calls]) => (
                            <Badge key={tool}>{`${tool}: ${calls}`}</Badge>
                        ))}
                        {summary.truncated ? <Badge variant="warning">resumo truncado — estreite o período</Badge> : null}
                    </div>
                ) : null}

                {/* Conexões — cada login é um grant; revogar mata na hora */}
                <div>
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-mono-500">Conexões</h3>
                    {grants.length === 0 ? (
                        <p className="text-sm text-mono-500">Nenhuma conexão MCP registrada para este usuário.</p>
                    ) : (
                        <div className="space-y-2">
                            {grants.map((grant) => (
                                <div
                                    key={grant.id}
                                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-mono-200 px-3 py-2 dark:border-mono-800"
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-black dark:text-white">
                                            {grant.client_name || 'Cliente sem nome'}
                                            {grant.active ? (
                                                <Badge variant="success" className="ml-2">
                                                    ativa
                                                </Badge>
                                            ) : (
                                                <Badge variant="danger" className="ml-2">
                                                    revogada
                                                </Badge>
                                            )}
                                        </p>
                                        <p className="text-xs text-mono-500">
                                            login {FormatDateTime(grant.created_at)}
                                            {' · última atividade '}
                                            {FormatDateTime(grant.last_activity_at)}
                                        </p>
                                    </div>
                                    {grant.active && canRevoke ? (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            loading={revoking === grant.id}
                                            onClick={() => handleRevoke(grant)}
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            Revogar
                                        </Button>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Histórico — o filtro de "quais tools ele está usando" */}
                <div>
                    <div className="mb-2 flex flex-wrap items-end gap-3">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-mono-500">Histórico</h3>
                        <Select
                            label="Tool"
                            value={toolFilter}
                            onChange={(event) => setToolFilter(event.target.value)}
                            className="w-44"
                        >
                            <option value="">Todas</option>
                            {MCP_TOOLS.map((tool) => (
                                <option key={tool} value={tool}>
                                    {tool}
                                </option>
                            ))}
                        </Select>
                        <Select
                            label="Desfecho"
                            value={outcomeFilter}
                            onChange={(event) => setOutcomeFilter(event.target.value)}
                            className="w-36"
                        >
                            <option value="">Todos</option>
                            <option value="ok">ok</option>
                            <option value="denied">recusada</option>
                            <option value="error">erro</option>
                        </Select>
                    </div>
                    {loading ? (
                        <p className="text-sm text-mono-500">Carregando…</p>
                    ) : requests.length === 0 ? (
                        <p className="text-sm text-mono-500">Nenhuma chamada no recorte.</p>
                    ) : (
                        <div className="max-h-72 overflow-auto rounded-lg border border-mono-200 dark:border-mono-800">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-mono-50 dark:bg-mono-900">
                                    <tr className="text-left text-xs uppercase tracking-wide text-mono-500">
                                        <th className="px-3 py-2">Quando</th>
                                        <th className="px-3 py-2">Tool</th>
                                        <th className="px-3 py-2">Alvo</th>
                                        <th className="px-3 py-2">Desfecho</th>
                                        <th className="px-3 py-2">ms</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests.map((row) => (
                                        <tr key={row.id} className="border-t border-mono-100 dark:border-mono-800">
                                            <td className="whitespace-nowrap px-3 py-1.5 text-mono-600 dark:text-mono-400">
                                                {FormatDateTime(row.created_at)}
                                            </td>
                                            <td className="px-3 py-1.5 font-mono text-xs">{row.tool}</td>
                                            <td
                                                className="max-w-[220px] truncate px-3 py-1.5 font-mono text-xs"
                                                title={row.target || ''}
                                            >
                                                {row.target || '—'}
                                            </td>
                                            <td className="px-3 py-1.5">
                                                <Badge variant={OUTCOME_BADGE[row.outcome] || 'secondary'}>
                                                    {row.outcome}
                                                </Badge>
                                            </td>
                                            <td className="px-3 py-1.5 text-mono-600 dark:text-mono-400">
                                                {row.duration_ms}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default UserMcpPanel;
